import { motion } from "framer-motion";
import { ConciergeBell, ShoppingCart, GlassWater, Utensils, UtensilsCrossed, Soup, Sparkles, HandHelping, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { BottomSheet } from "@/components/BottomSheet";
import { useUI } from "@/context/UIContext";
import { useOrder } from "@/context/OrderContext";
import { useLang } from "@/context/LanguageContext";
import { isOnline, usesRealBackend } from "@/lib/network";

const SERVICE_ERROR_KEY = {
  network: "offlineActionBlocked", rate_limited: "orderRateLimited", table_required: "orderTableRequired",
  table_session_required: "tableSessionRequired",
};

export function CallWaiterSheet() {
  const { waiterOpen, closeWaiter } = useUI();
  const { requestService } = useOrder();
  const { t } = useLang();

  const choices = [
    { key: "waiterOrder", icon: ShoppingCart },
    { key: "waiterWater", icon: GlassWater },
    { key: "waiterNapkin", icon: Sparkles },
    { key: "waiterCutlery", icon: UtensilsCrossed },
    { key: "waiterSauce", icon: Soup },
    { key: "waiterClean", icon: Utensils },
    { key: "waiterRequest", icon: HandHelping },
    { key: "waiterOther", icon: MoreHorizontal },
  ];

  const submit = async (choiceKey) => {
    if (!isOnline()) {
      toast.error(t("offlineActionBlocked"));
      return;
    }
    const result = await requestService("waiter", choiceKey);
    if (!result.ok) {
      toast.error(t(SERVICE_ERROR_KEY[result.reason] || "serviceFailed"));
      return;
    }
    closeWaiter();
    toast.success(t("waiterNotified"), { description: t("waiterNotifiedDesc"), icon: <ConciergeBell className="h-4 w-4" /> });
    // "On the way" comes from the real request status (staff claim); mock mode keeps the demo timer.
    if (!usesRealBackend()) {
      setTimeout(() => {
        toast(t("waiterOnWay"), { icon: <ConciergeBell className="h-4 w-4 text-primary" /> });
      }, 2600);
    }
  };

  return (
    <BottomSheet open={waiterOpen} onClose={closeWaiter} maxHeight="80vh" testId="call-waiter-sheet">
      <div className="px-5 pb-6 pt-1">
        <div className="mb-5 flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-2xl brand-gradient text-white">
            <ConciergeBell className="h-6 w-6" />
          </div>
          <h2 className="font-heading text-xl font-extrabold">{t("waiterTitle")}</h2>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {choices.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.button
                key={c.key}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => submit(c.key)}
                className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3.5 text-start shadow-soft transition-colors hover:border-primary/40"
                data-testid={`waiter-choice-${c.key}`}
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-secondary text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="font-heading text-[13px] font-bold leading-tight">{t(c.key)}</span>
              </motion.button>
            );
          })}
        </div>
      </div>
    </BottomSheet>
  );
}
